import { useState, useEffect } from 'react';
import { Copy, Check, RefreshCw, Globe, Lock, ExternalLink, Code2 } from '@/components/icons';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import {
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
} from '@/components/ui/beui';
import { toggleDiagramSharing, regenerateShareToken } from '../../services/diagramService';
import type { ShareToggleResponse } from '../../types';

type TabType = 'share' | 'embed';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  diagramId: string;
  diagramName: string;
  initialIsPublic: boolean;
  initialShareToken: string | null;
  onShareUpdate?: (data: ShareToggleResponse) => void;
}

export default function ShareModal({
  isOpen,
  onClose,
  diagramId,
  diagramName,
  initialIsPublic,
  initialShareToken,
  onShareUpdate,
}: ShareModalProps) {
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [shareToken, setShareToken] = useState(initialShareToken);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('share');

  // Embed options
  const [embedTheme, setEmbedTheme] = useState<'auto' | 'dark' | 'light'>('auto');
  const [embedWidth, setEmbedWidth] = useState('100%');
  const [embedHeight, setEmbedHeight] = useState('500');

  // Reset state when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setIsPublic(initialIsPublic);
      setShareToken(initialShareToken);
      setError(null);
      setCopied(false);
    }
  }, [isOpen, initialIsPublic, initialShareToken]);

  const shareUrl = shareToken
    ? `${window.location.origin}/share/${shareToken}`
    : null;

  const embedUrl = shareToken
    ? `${window.location.origin}/embed/${shareToken}?theme=${embedTheme}`
    : null;

  const embedCode = embedUrl
    ? `<iframe src="${embedUrl}" width="${embedWidth}" height="${embedHeight}" frameborder="0" style="border-radius: 8px; border: 1px solid #e2e8f0;" allowfullscreen></iframe>`
    : '';

  const handleCopyEmbed = async () => {
    if (!embedCode) return;

    try {
      await navigator.clipboard.writeText(embedCode);
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch {
      setError('Failed to copy embed code');
    }
  };

  const handleToggleSharing = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await toggleDiagramSharing(diagramId, !isPublic);
      setIsPublic(result.isPublic);
      setShareToken(result.shareToken);
      onShareUpdate?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateToken = async () => {
    setIsRegenerating(true);
    setError(null);

    try {
      const result = await regenerateShareToken(diagramId);
      setShareToken(result.shareToken);
      onShareUpdate?.(result);
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate link');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleOpenLink = () => {
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        {/* Header */}
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
          <div className={`rounded-md p-1.5 ${isPublic ? 'bg-primary/10' : 'bg-muted'}`}>
            {isPublic ? (
              <Globe className="h-4 w-4 text-primary" />
            ) : (
              <Lock className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold text-foreground">
              Share diagram
            </DialogTitle>
            <p className="truncate text-xs text-muted-foreground">{diagramName}</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {/* Toggle row */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Public access</p>
              <p className="text-xs text-muted-foreground">
                {isPublic
                  ? 'Anyone with the link can view (view-only)'
                  : 'Only you can view this diagram'}
              </p>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={() => handleToggleSharing()}
              disabled={isLoading}
              ariaLabel="Public access"
              className="flex-none"
            />
          </div>

          {/* Tabs - only show when public */}
          {isPublic && shareToken && (
            <>
              <div className="mt-4 flex gap-1 border-b border-border">
                <button
                  onClick={() => setActiveTab('share')}
                  className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
                    activeTab === 'share'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Link
                </button>
                <button
                  onClick={() => setActiveTab('embed')}
                  className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
                    activeTab === 'embed'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Code2 className="h-3.5 w-3.5" />
                  Embed
                </button>
              </div>

              {/* Share Tab Content */}
              {activeTab === 'share' && (
                <div className="mt-4 space-y-3">
                  <div className="flex h-9 items-center gap-1 rounded-lg border border-border bg-card pl-3 pr-1">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl || ''}
                      aria-label="Share link"
                      className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground outline-none"
                      onFocus={(e) => e.target.select()}
                    />
                    <Tooltip content="Copy link">
                      <button
                        onClick={handleCopyLink}
                        aria-label="Copy link"
                        className={`grid h-7 w-7 flex-none place-items-center rounded-md transition-colors ${
                          copied
                            ? 'text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </Tooltip>
                    <Tooltip content="Open in new tab">
                      <button
                        onClick={handleOpenLink}
                        aria-label="Open in new tab"
                        className="grid h-7 w-7 flex-none place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  </div>

                  <Tooltip content="Generating a new link will invalidate the previous one">
                    <button
                      onClick={handleRegenerateToken}
                      disabled={isRegenerating}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
                      {isRegenerating ? 'Generating…' : 'Generate new link'}
                      <span className="text-muted-foreground/70">— invalidates the previous one</span>
                    </button>
                  </Tooltip>
                </div>
              )}

              {/* Embed Tab Content */}
              {activeTab === 'embed' && (
                <div className="mt-4 space-y-3">
                  {/* Embed Options */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Theme
                      </label>
                      <Select
                        value={embedTheme}
                        onValueChange={(v) => setEmbedTheme(v as 'auto' | 'dark' | 'light')}
                        radius={8}
                        className="w-full"
                      >
                        <SelectTrigger className="h-8 rounded-md bg-card px-2 py-0 text-[13px]">
                          <SelectValue placeholder="Theme" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto" className="text-[13px]">Auto</SelectItem>
                          <SelectItem value="dark" className="text-[13px]">Dark</SelectItem>
                          <SelectItem value="light" className="text-[13px]">Light</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Width
                      </label>
                      <input
                        type="text"
                        value={embedWidth}
                        onChange={(e) => setEmbedWidth(e.target.value)}
                        placeholder="100%"
                        className="h-8 w-full rounded-md border border-border bg-card px-2 text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Height
                      </label>
                      <input
                        type="text"
                        value={embedHeight}
                        onChange={(e) => setEmbedHeight(e.target.value)}
                        placeholder="500"
                        className="h-8 w-full rounded-md border border-border bg-card px-2 text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                  </div>

                  {/* Embed Code */}
                  <div className="relative">
                    <pre className="max-h-28 overflow-x-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-card p-3 pr-16 text-[11px] leading-relaxed text-muted-foreground">
                      {embedCode}
                    </pre>
                    <button
                      onClick={handleCopyEmbed}
                      className={`absolute right-2 top-2 flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                        copiedEmbed
                          ? 'text-primary'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {copiedEmbed ? (
                        <>
                          <Check className="h-3 w-3" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>

                  {/* URL Parameters Info */}
                  <div className="rounded-lg bg-muted/50 p-2.5 dark:bg-card/50">
                    <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
                      Additional URL parameters
                    </p>
                    <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <li><code className="rounded bg-muted px-1 dark:bg-muted">controls=true</code> zoom controls</li>
                      <li><code className="rounded bg-muted px-1 dark:bg-muted">minimap=true</code> minimap</li>
                      <li><code className="rounded bg-muted px-1 dark:bg-muted">background=false</code> hide grid</li>
                      <li><code className="rounded bg-muted px-1 dark:bg-muted">branding=false</code> hide watermark</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
